import { apiClient } from "../core/apiClient";

export interface ICartItem {
  id: number;
  productId: string;
  quantity: number;
  unitPrice: string;
}

export interface IAddToCartData {
  productId: string;
  quantity: number;
}

export interface IUpdateCartItemData {
  quantity: number;
}

export interface IAnonymousSessionData {
  sessionToken: string;
  expiresAt: string;
}

export interface IAnonymousClientData {
  nombre: string;
  email: string;
  telefono?: string;
}

class CartService {
  // ✅ MÉTODOS PARA USUARIOS ANÓNIMOS

  /**
   * Genera un token de sesión anónima
   */
  static async generateAnonymousSession(): Promise<IAnonymousSessionData> {
    const response = await apiClient.post("/api/v1/cart/generate-session");
    return response.data;
  }

  /**
   * Obtiene los items del carrito anónimo
   */
  static async getAnonymousCartItems(
    sessionToken: string
  ): Promise<ICartItem[]> {
    const response = await apiClient.get("/api/v1/cart/anonymous/get-items", {
      headers: {
        "x-session-token": sessionToken,
      },
    });
    return response.data;
  }

  /**
   * Agrega un item al carrito anónimo
   */
  static async addToAnonymousCart(
    data: IAddToCartData,
    sessionToken: string
  ): Promise<ICartItem> {
    console.log("creando item en el carrito anónimo en el backend");
    const response = await apiClient.post(
      "/api/v1/cart/anonymous/add-item",
      data,
      {
        headers: {
          "x-session-token": sessionToken,
        },
      }
    );
    return response.data;
  }

  /**
   * Actualiza la cantidad de un item del carrito anónimo
   */
  static async updateAnonymousCartItemQuantity(
    cartItemId: string,
    data: IUpdateCartItemData,
    sessionToken: string
  ): Promise<ICartItem> {
    const response = await apiClient.put(
      "/api/v1/cart/anonymous/update-quantity",
      { cartItemId, ...data },
      {
        headers: {
          "x-session-token": sessionToken,
        },
      }
    );
    return response.data;
  }

  /**
   * Elimina un item del carrito anónimo
   */
  static async removeAnonymousCartItem(
    cartItemId: string,
    sessionToken: string
  ): Promise<void> {
    await apiClient.delete(`/api/v1/cart/anonymous/remove-item/${cartItemId}`, {
      headers: {
        "x-session-token": sessionToken,
      },
    });
  }

  /**
   * Limpia todo el carrito anónimo
   */
  static async clearAnonymousCart(sessionToken: string): Promise<void> {
    await apiClient.delete("/api/v1/cart/anonymous/clear-cart", {
      headers: {
        "x-session-token": sessionToken,
      },
    });
  }

  /**
   * Actualiza los datos del cliente anónimo
   */
  static async updateAnonymousClientData(
    clientData: IAnonymousClientData,
    sessionToken: string
  ): Promise<any> {
    const response = await apiClient.put(
      "/api/v1/cart/anonymous/update-client-data",
      clientData,
      {
        headers: {
          "x-session-token": sessionToken,
        },
      }
    );
    return response.data;
  }

  /**
   * Convierte un carrito anónimo a autenticado
   */
  static async convertAnonymousToAuthenticated(
    sessionToken: string
  ): Promise<any> {
    const response = await apiClient.post("/api/v1/cart/convert-anonymous", {
      sessionToken,
    });
    return response.data;
  }

  // ✅ MÉTODOS PARA USUARIOS AUTENTICADOS (EXISTENTES)

  /**
   * Obtiene los items del carrito de un usuario autenticado
   * Nota: Para usuarios autenticados, el token JWT se envía automáticamente en el header Authorization
   */
  static async getCartItems(userId: string): Promise<ICartItem[]> {
    const response = await apiClient.get(`/api/v1/cart/get-items/${userId}`);
    return response.data;
  }

  /**
   * Agrega un item al carrito de un usuario autenticado
   * Nota: Para usuarios autenticados, el token JWT se envía automáticamente en el header Authorization
   */
  static async addToCart(data: IAddToCartData): Promise<ICartItem> {
    console.log("creando item en el carrito en el backend");
    const response = await apiClient.post("/api/v1/cart/add-item", data);
    return response.data;
  }

  /**
   * Actualiza la cantidad de un item del carrito de un usuario autenticado
   * Nota: Para usuarios autenticados, el token JWT se envía automáticamente en el header Authorization
   */
  static async updateCartItemQuantity(
    cartItemId: string,
    data: IUpdateCartItemData
  ): Promise<ICartItem> {
    // El backend espera el cartItemId y la nueva cantidad en el cuerpo de la petición
    const response = await apiClient.put("/api/v1/cart/update-quantity", {
      cartItemId,
      ...data,
    });
    return response.data;
  }

  /**
   * Elimina un item del carrito de un usuario autenticado
   * Nota: Para usuarios autenticados, el token JWT se envía automáticamente en el header Authorization
   */
  static async removeCartItem(cartItemId: string): Promise<void> {
    await apiClient.delete(`/api/v1/cart/remove-item/${cartItemId}`);
  }

  /**
   * Limpia todo el carrito de un usuario autenticado
   * Nota: Para usuarios autenticados, el token JWT se envía automáticamente en el header Authorization
   */
  static async clearCart(userId: string): Promise<void> {
    await apiClient.delete(`/api/v1/cart/clear-cart/${userId}`);
  }
}

export default CartService;
